/**
 * Container operations — CRUD for projects, lists, areas, and sections.
 */

import { ObjectStatus, ObjectType } from "./db-types.js"
import type { DbObject } from "./db-types.js"
import { getDb, getNow, emitEvent } from "./runtime.js"
import { createTaskDefaults } from "./utils.js"
import { ok, fail } from "./op-types.js"
import type { OpResult, ContainerOpts } from "./op-types.js"
import {
  getInsertIndex,
  getSidebarInsertIndex,
} from "./helpers/indexHelpers.js"
import { resolveContainerRealmId } from "./helpers/realmHelpers.js"

// ---------------------------------------------------------------------------
// addProject
// ---------------------------------------------------------------------------

export async function addProject(
  title: string,
  opts?: ContainerOpts,
): Promise<OpResult<{ id: string }>> {
  const db = getDb()
  try {
    const id = opts?.id ?? crypto.randomUUID()
    const timestamp = getNow()
    const parentPath = opts?.parentPath || "root"

    const isRootBeforeArea =
      parentPath === "root" &&
      (opts?.insertPosition === "beforeFirstArea" || !opts?.insertPosition)

    const index = isRootBeforeArea
      ? await getSidebarInsertIndex()
      : await getInsertIndex(
          parentPath,
          opts?.insertPosition ?? "bottom",
          ObjectType.Project,
        )

    const project = createTaskDefaults({
      id,
      type: ObjectType.Project,
      title,
      parentPath,
      index,
      realmId:
        opts?.realmId ?? (await resolveContainerRealmId(parentPath || "root")),
      owner: opts?.owner ?? "",
      createdAt: timestamp,
      updatedAt: timestamp,
    })

    if (isRootBeforeArea) {
      await db.transaction("rw", [db.totodo, db.privateIndex], async () => {
        await db.totodo.add(project)
        await db.privateIndex.put({ id: `#${id}`, index })
      })
    } else {
      await db.totodo.add(project)
    }
    emitEvent("project_created", { id })
    return ok({ id })
  } catch (e) {
    return fail(
      `addProject failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// addList
// ---------------------------------------------------------------------------

export async function addList(
  title: string,
  opts?: ContainerOpts,
): Promise<OpResult<{ id: string }>> {
  const db = getDb()
  try {
    const id = opts?.id ?? crypto.randomUUID()
    const timestamp = getNow()
    const parentPath = opts?.parentPath || "root"

    const isRootBeforeArea =
      parentPath === "root" &&
      (opts?.insertPosition === "beforeFirstArea" || !opts?.insertPosition)

    const index = isRootBeforeArea
      ? await getSidebarInsertIndex()
      : await getInsertIndex(
          parentPath,
          opts?.insertPosition ?? "bottom",
          ObjectType.List,
        )

    const list = createTaskDefaults({
      id,
      type: ObjectType.List,
      title,
      parentPath,
      index,
      realmId:
        opts?.realmId ?? (await resolveContainerRealmId(parentPath || "root")),
      owner: opts?.owner ?? "",
      createdAt: timestamp,
      updatedAt: timestamp,
    })

    if (isRootBeforeArea) {
      await db.transaction("rw", [db.totodo, db.privateIndex], async () => {
        await db.totodo.add(list)
        await db.privateIndex.put({ id: `#${id}`, index })
      })
    } else {
      await db.totodo.add(list)
    }
    emitEvent("list_created", { id })
    return ok({ id })
  } catch (e) {
    return fail(`addList failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// addArea
// ---------------------------------------------------------------------------

export async function addArea(
  title: string,
  opts?: ContainerOpts,
): Promise<OpResult<{ id: string }>> {
  const db = getDb()
  try {
    const id = opts?.id ?? crypto.randomUUID()
    const timestamp = getNow()
    const parentPath = opts?.parentPath || "root"

    const index = await getInsertIndex(
      parentPath,
      opts?.insertPosition ?? "bottom",
      ObjectType.Area,
    )

    const area = createTaskDefaults({
      id,
      type: ObjectType.Area,
      title,
      parentPath,
      index,
      realmId:
        opts?.realmId ?? (await resolveContainerRealmId(parentPath || "root")),
      owner: opts?.owner ?? "",
      createdAt: timestamp,
      updatedAt: timestamp,
    })

    await db.totodo.add(area)
    emitEvent("area_created", { id })
    return ok({ id })
  } catch (e) {
    return fail(`addArea failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// addSection
// ---------------------------------------------------------------------------

export async function addSection(
  title: string,
  parentPath: string,
  opts?: Pick<ContainerOpts, "insertPosition" | "id">,
): Promise<OpResult<{ id: string }>> {
  const db = getDb()
  try {
    const id = opts?.id ?? crypto.randomUUID()
    const timestamp = getNow()

    const index = await getInsertIndex(
      parentPath,
      opts?.insertPosition ?? "bottom",
      ObjectType.Section,
    )

    const section = createTaskDefaults({
      id,
      type: ObjectType.Section,
      title,
      parentPath,
      index,
      realmId: await resolveContainerRealmId(parentPath),
      createdAt: timestamp,
      updatedAt: timestamp,
    })

    await db.totodo.add(section)
    return ok({ id })
  } catch (e) {
    return fail(
      `addSection failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// renameContainer
// ---------------------------------------------------------------------------

export async function renameContainer(
  id: string,
  title: string,
): Promise<OpResult> {
  const db = getDb()
  try {
    await db.totodo.update(id, { title, updatedAt: getNow() })
    return ok()
  } catch (e) {
    return fail(
      `renameContainer failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// updateContainerIcon
// ---------------------------------------------------------------------------

/**
 * Set or clear the custom icon on a container (project, list, or area).
 * Pass `undefined` to reset to the default TimelineIcon.
 */
export async function updateContainerIcon(
  id: string,
  icon: string | undefined,
): Promise<OpResult> {
  const db = getDb()
  try {
    await db.totodo.update(id, { icon, updatedAt: getNow() })
    return ok()
  } catch (e) {
    return fail(
      `updateContainerIcon failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Delete: the app's own container deletes (sidebar / context menu / project header)
// ---------------------------------------------------------------------------

/**
 * Soft-delete a project / list and ALL its children (the objects at `root/<id>`: tasks, sections).
 * A project's children live at `root/<id>` wherever the project itself sits, also inside an area.
 *
 * This is the app's delete for a project or list (sidebar, context menu, project header); the SPA
 * and the server's delete_container both call this one function, so the cascade is the same.
 * Like the app, it stamps the project and its direct, live children with one deletedAt and leaves
 * the checklist rows under those tasks alone (they go with their task and come back with it).
 */
export async function deleteProjectWithChildren(projectId: string): Promise<void> {
  const db = getDb()
  const timestamp = getNow()

  await db.transaction("rw", db.totodo, async () => {
    await db.totodo.update(projectId, {
      isDeleted: 1,
      deletedAt: timestamp,
      updatedAt: timestamp,
    })

    const children = await db.totodo
      .where("[parentPath+isDeleted]")
      .equals([`root/${projectId}`, 0])
      .toArray()
    if (children.length > 0) {
      await db.totodo.bulkUpdate(
        children.map((c) => ({
          key: c.id as string,
          changes: { isDeleted: 1 as const, deletedAt: timestamp, updatedAt: timestamp },
        })),
      )
    }
  })
}

/**
 * Soft-delete a section and, with `includeChildren`, its positional children (`childIds`: what the
 * project list shows between this section and the next). Without it only the section goes and its
 * tasks become "loose". The app's delete for a section (TaskItem), shared with delete_container.
 */
export async function deleteSection(
  sectionId: string,
  includeChildren: boolean,
  childIds?: string[],
): Promise<void> {
  const db = getDb()
  const timestamp = getNow()

  await db.transaction("rw", db.totodo, async () => {
    await db.totodo.update(sectionId, {
      isDeleted: 1,
      deletedAt: timestamp,
      updatedAt: timestamp,
    })

    if (includeChildren && childIds && childIds.length > 0) {
      const children = await db.totodo.bulkGet(childIds)
      const updates = children
        .filter((c): c is DbObject => c !== undefined && c.isDeleted !== 1)
        .map((c) => ({
          key: c.id as string,
          changes: { isDeleted: 1 as const, deletedAt: timestamp, updatedAt: timestamp },
        }))
      if (updates.length > 0) await db.totodo.bulkUpdate(updates)
    }
  })
}

/**
 * The ids of a section's positional children, as the project list shows them: the unfinished,
 * live tasks (and sub-projects) after the section, in index order, up to the next section. The
 * app passes the same set (its visible list) to deleteSection; the server, which has no list on
 * screen, computes it here.
 */
export async function getSectionChildIds(sectionId: string): Promise<string[]> {
  const db = getDb()
  const section = await db.totodo.get(sectionId)
  if (!section || section.type !== ObjectType.Section) return []
  const siblings = (
    await db.totodo.where("[parentPath+isDeleted]").equals([section.parentPath, 0]).toArray()
  )
    .filter((o) => o.type !== ObjectType.Checklist)
    .sort((a, b) => a.index - b.index)
  const at = siblings.findIndex((o) => o.id === sectionId)
  if (at === -1) return []
  const ids: string[] = []
  for (let i = at + 1; i < siblings.length; i++) {
    const o = siblings[i]!
    if (o.type === ObjectType.Section) break
    if (o.status === ObjectStatus.Unfinished && o.id) ids.push(o.id)
  }
  return ids
}
